import axios from '@/services/axios'

const fetchBookingsData = async (
	data: FilterPayload,
): Promise<{ data: Booking[]; pagination: Pagination }> => {
	const queryParams = new URLSearchParams()
	queryParams.append('page', data.pageNumber)
	queryParams.append('limit', data.pageSize)

	if (data.filterColumn && data.filterValue) {
		queryParams.append('filterColumn', data.filterColumn)
		queryParams.append('filterValue', data.filterValue)
	}

	const queryString = queryParams.toString()

	const response = await axios.get(`/booking/all?${queryString}`)
	return response.data
}

const getExtendFeasibility = async (
	bookingId: Booking['id'],
	endDate: Booking['end'],
): Promise<{
	sevenDayOverlap: string
	fourteenDayOverlap: string
	twentyOneDayOverlap: string
	twentyEightDayOverlap: string
}> => {
	const response = await axios.get(
		`/booking/get-extend-feasibility?standId=${bookingId}&bookingEndDate=${endDate}`,
	)
	return response.data?.response?.data
}

const verifyStandAvailability = async (standId: number, start_date: string, end_date: string) => {
	const response = await axios.get(
		`/booking/check-availability?stand_id=${standId}&start_date=${start_date}&end_date=${end_date}`,
	)
	return response.data
}

const updateBooking = async (
	bookingId: Booking['id'],
	data: { end: string; rent_price: number; paypal_code: string; title: string },
) => {
	const response = await axios.put(`/booking/${bookingId}`, data)
	return response.data
}

const generateInvoice = async (data: Invoice) => {
	const response = await axios.post('/booking/generate-invoice', data)
	return response.data
}

const fetchCollectionsData = async (
	data: FilterPayload,
): Promise<{ data: Collection[]; pagination: Pagination }> => {
	const queryParams = new URLSearchParams()
	queryParams.append('page', data.pageNumber)
	queryParams.append('limit', data.pageSize)

	if (data.filterColumn && data.filterValue) {
		queryParams.append('filterColumn', data.filterColumn)
		queryParams.append('filterValue', data.filterValue)
	}

	if (data.collectionStatus) {
		queryParams.append('collection_status', data.collectionStatus)
	}

	const queryString = queryParams.toString()

	const response = await axios.get(`/booking/all-collect-bookings?${queryString}`)
	return response.data
}

const fetchStandsData = async (
	startDate: Booking['start'],
	endDate: Booking['end'],
): Promise<Stand[]> => {
	const response = await axios.get(
		`/stand/available_stands_by_dates?startDate=${startDate}&endDate=${endDate}`,
	)
	return response.data.data
}

const verifyCouponCode = async (
	code: string,
	startDate: string,
	packageId: number,
	id?: User['id'],
): Promise<{ data: Coupon; message: string; status: boolean }> => {
	const response = await axios.get(
		id
			? `/couponRoute/verifyCoupon/${code}/${id}?startDate=${startDate}&booking_type=${packageId}`
			: `/couponRoute/verifyCoupon/${code}/-1?startDate=${startDate}&booking_type=${packageId}`,
	)
	return response.data
}

const verifyReferralCode = async (
	code: string,
	email: string,
): Promise<{ data: Referral; message: string; status: boolean }> => {
	const response = await axios.get(`/user/verifyReferralCode/${code}/${email}`)
	return response.data
}

const fetchHolidays = async (): Promise<{ data: Holiday[]; pagination: Pagination }> => {
	const response = await axios.get(`/holidays`)
	return response.data
}

const placeAdminOrder = async (data: any) => {
	const response = await axios.post('booking/admin_place_order?sendEmail=false', data)
	return response.data
}

const placeOrder = async (data: any) => {
	const response = await axios.post('booking/place_order?sendEmail=true', data)
	return response.data
}

const sendBookingEmail = async (data: any) => {
	const response = await axios.post('/booking/email', data)
	return response.data
}

const updateAdminBooking = async (
	bookindId: Booking['id'],
	data: { stand_id: string; startDate: string; endDate: string; rentPrice: string },
) => {
	const response = await axios.put(`/booking/update-booking/booking-id/${bookindId}`, data)
	return response.data
}

const updateCollectionStatus = async (id: number, status: string) => {
	const response = await axios.post(`/booking/update-user-bookings-collect/${id}`, {
		action: status,
	})
	return response.data
}

const updateNote = async (data: { user_notes: string } | { booking_notes: string }, id: number) => {
	const response = await axios.put(`/booking/update-notes/booking-id/${id}`, data)
	return response.data
}

const fetchSalesRecordsByUser = async (userId: number): Promise<SalesRecord[]> => {
	const response = await axios.get(`/sales-records/user/${userId}`)
	return response.data?.response?.data ?? []
}

const replaceSalesRecords = async (data: {
	user_id: number
	records: Array<{
		booking_id: number
		user_id: number
		total_sales: number
		commission: number
		amount_paid_out: number
		net_commission: number
		invoice_number: string | null
		comments: string | null
	}>
}) => {
	const response = await axios.post('/sales-records/bulk', data)
	return response.data
}

const deleteSalesRecord = async (id: number) => {
	const response = await axios.delete(`/sales-records/${id}`)
	return response.data
}

const updatePaymentStatus = async (id: number, status: string) => {
	const response = await axios.post(`/booking/update-user-bookings-earnings/${id}`, {
		action: status,
	})
	return response.data
}

const updateCustomerBooking = async (userId: number) => {
	const response = await axios.get(`/booking/get-admin-bookings-earnings/${userId}`)
	return response.data
}

const cancelBooking = async (id: number) => {
	const response = await axios.get(`/booking/cancel-booking/booking-id/${id}`)
	return response.data
}

const bookingService = {
	fetchBookingsData,
	fetchStandsData,
	fetchHolidays,
	fetchCollectionsData,
	getExtendFeasibility,
	updateBooking,
	verifyCouponCode,
	verifyReferralCode,
	verifyStandAvailability,
	updateAdminBooking,
	placeOrder,
	placeAdminOrder,
	sendBookingEmail,
	updatePaymentStatus,
	updateCollectionStatus,
	updateCustomerBooking,
	generateInvoice,
	updateNote,
	fetchSalesRecordsByUser,
	replaceSalesRecords,
	deleteSalesRecord,
	cancelBooking,
}

export default bookingService
